import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import "./foyda.css";
import { useSklad } from "../SkladContext";

const normDecStr = (v) => {
  if (v == null) return "0";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (s === "" || s === "-" || s === "." || s === "-.") return "0";
  return s;
};

const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};

const money = (v, { minFrac = 2, maxFrac = 2, suffix = " so'm" } = {}) =>
  toNum(v).toLocaleString("uz-UZ", {
    minimumFractionDigits: minFrac,
    maximumFractionDigits: maxFrac,
  }) + suffix;

const fmtQty = (v, { minFrac = 0, maxFrac = 3 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", {
    minimumFractionDigits: minFrac,
    maximumFractionDigits: maxFrac,
  });

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("uz-UZ");
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("uz-UZ", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const cx = (...items) => items.filter(Boolean).join(" ");

const sum = (arr, pick) =>
  arr.reduce((total, item) => total + (pick ? pick(item) : toNum(item)), 0);

const customerImg = (seller) =>
  seller?.avatar ||
  seller?.mijoz_rasmi_url ||
  seller?.mijoz_rasmi ||
  (seller?.mijozrasminomi ? `/media/${seller.mijozrasminomi}` : "");

const mapSellerRecord = (seller) => ({
  id: Number(seller?.id),
  name: seller?.ismi || seller?.name || "",
  phone: seller?.telraqam1 || seller?.telraqam2 || seller?.phone || "",
  avatar: customerImg(seller),
});

const saleTotals = (sale) => {
  let qty = 0;
  let revenue = 0;
  let profit = 0;

  sale.items.forEach((item) => {
    qty += toNum(item.qty);
    revenue += toNum(item.lineTotal);
    profit += toNum(item.lineProfit);
  });

  return { qty, revenue, profit };
};

const buildSales = (rows, sellerMap) => {
  const groups = new Map();

  rows.forEach((row) => {
    const sellerId = Number(row?.sotuvchi);
    const seller = sellerMap.get(sellerId);
    const orderId = row?.client_order_id ? `${sellerId}:${row.client_order_id}` : `sale:${row.id}`;
    const createdAt = row?.created_at || null;

    if (!groups.has(orderId)) {
      groups.set(orderId, {
        id: orderId,
        sellerId,
        sellerName: seller?.name || row?.sotuvchi_ismi || "Noma'lum mijoz",
        sellerPhone: seller?.phone || row?.sotuvchi_telraqam1 || "",
        sellerAvatar: seller?.avatar || "",
        note: row?.izoh || "",
        soldBy: row?.created_by_username || "",
        createdAt,
        payTypes: new Set(),
        items: [],
      });
    }

    const group = groups.get(orderId);
    group.payTypes.add(row?.tolov_turi || "");
    if (!group.note && row?.izoh) group.note = row.izoh;
    if (!group.soldBy && row?.created_by_username) group.soldBy = row.created_by_username;
    if (!group.createdAt || new Date(createdAt) > new Date(group.createdAt)) {
      group.createdAt = createdAt;
    }
    if (!group.sellerAvatar && seller?.avatar) group.sellerAvatar = seller.avatar;
    if (!group.sellerPhone && (seller?.phone || row?.sotuvchi_telraqam1)) {
      group.sellerPhone = seller?.phone || row?.sotuvchi_telraqam1 || "";
    }

    group.items.push({
      id: row?.id,
      product: row?.maxsulot_nom || "Noma'lum mahsulot",
      sku: row?.maxsulot_seriyasi || "-",
      tanNarx: row?.maxsulot_tan_narxi ?? 0,
      sotilganNarx: row?.narx ?? 0,
      qty: row?.sotilgansoni ?? 0,
      payType: row?.tolov_turi || "",
      date: createdAt,
      lineTotal:
        row?.line_total ??
        toNum(row?.narx) * toNum(row?.sotilgansoni),
      lineProfit:
        row?.line_profit ??
        (toNum(row?.narx) - toNum(row?.maxsulot_tan_narxi)) * toNum(row?.sotilgansoni),
    });
  });

  return Array.from(groups.values())
    .map((group) => {
      const items = [...group.items].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
      );
      const payTypes = Array.from(group.payTypes).filter(Boolean);
      let payLabel = "Aralash";
      if (payTypes.length === 1) {
        payLabel = payTypes[0] === "naqd" ? "Naqd" : "Nasiya";
      }

      return {
        ...group,
        payLabel,
        items,
      };
    })
    .sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
};

export default function FoydaSavdoPage() {
  const { activeSklad } = useSklad();
  const [sellers, setSellers] = useState([]);
  const [salesRows, setSalesRows] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [error, setError] = useState("");

  const [sellerQuery, setSellerQuery] = useState("");
  const [sellerOpen, setSellerOpen] = useState(false);
  const [activeSellerId, setActiveSellerId] = useState(null);
  const [productQuery, setProductQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [openMap, setOpenMap] = useState({});

  const sellerBoxRef = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (sellerBoxRef.current && !sellerBoxRef.current.contains(event.target)) {
        setSellerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSellers = async () => {
      setLoadingSellers(true);
      try {
        const { data } = await api.get("sotuvchi/", {
          params: { ordering: "ismi" },
        });
        if (cancelled) return;

        const rows = Array.isArray(data) ? data : data?.results || [];
        setSellers(rows.map(mapSellerRecord));
      } catch (e) {
        console.log("Foyda sellers load error", e?.response?.status, e?.response?.data);
        if (!cancelled) {
          setSellers([]);
        }
      } finally {
        if (!cancelled) setLoadingSellers(false);
      }
    };

    loadSellers();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;

    const loadSales = async () => {
      setLoadingSales(true);
      setError("");
      try {
        const params = { ordering: "-created_at" };
        if (activeSellerId) params.seller_ids = activeSellerId;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (activeSklad && activeSklad !== "all") params.sklad = activeSklad;

        const { data } = await api.get("sotilgan-maxsulot/", { params });
        if (cancelled) return;

        const rows = Array.isArray(data) ? data : data?.results || [];
        setSalesRows(rows);
      } catch (e) {
        console.log("Foyda sales load error", e?.response?.status, e?.response?.data);
        if (!cancelled) {
          setSalesRows([]);
          setError("Foyda ma'lumotlarini yuklashda xatolik yuz berdi.");
        }
      } finally {
        if (!cancelled) setLoadingSales(false);
      }
    };

    loadSales();
    return () => {
      cancelled = true;
    };
  }, [activeSellerId, dateFrom, dateTo, refreshKey, activeSklad]);

  const sellerMap = useMemo(
    () => new Map(sellers.map((seller) => [Number(seller.id), seller])),
    [sellers]
  );

  const groupedSales = useMemo(
    () => buildSales(salesRows, sellerMap),
    [salesRows, sellerMap]
  );

  const activeSeller = useMemo(() => {
    if (!activeSellerId) return null;
    return sellerMap.get(Number(activeSellerId)) || null;
  }, [activeSellerId, sellerMap]);

  const matchedSellers = useMemo(() => {
    const source = sellers.length
      ? sellers
      : Array.from(
          new Map(
            groupedSales.map((sale) => [
              sale.sellerId,
              {
                id: sale.sellerId,
                name: sale.sellerName,
                phone: sale.sellerPhone,
                avatar: sale.sellerAvatar,
              },
            ])
          ).values()
        );

    const q = sellerQuery.trim().toLowerCase();
    if (!q) return source.slice(0, 20);

    return source.filter((seller) =>
      `${seller.name} ${seller.phone}`.toLowerCase().includes(q)
    );
  }, [groupedSales, sellerQuery, sellers]);

  const filteredSales = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return groupedSales;

    return groupedSales
      .map((sale) => {
        const items = sale.items.filter((item) =>
          `${item.product} ${item.sku}`.toLowerCase().includes(q)
        );

        if (!items.length) return null;
        return { ...sale, items };
      })
      .filter(Boolean);
  }, [groupedSales, productQuery]);

  const headerTotals = useMemo(() => {
    const revenue = sum(filteredSales, (sale) => saleTotals(sale).revenue);
    const profit = sum(filteredSales, (sale) => saleTotals(sale).profit);
    const qty = sum(filteredSales, (sale) => saleTotals(sale).qty);
    return { revenue, profit, qty };
  }, [filteredSales]);

  const doExport = () => {
    const payload = {
      generated_at: new Date().toISOString(),
      filters: {
        seller_id: activeSellerId,
        seller_name: activeSeller?.name || null,
        product_query: productQuery,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      },
      summary: {
        revenue: headerTotals.revenue,
        profit: headerTotals.profit,
        qty: headerTotals.qty,
        sale_count: filteredSales.length,
      },
      sales: filteredSales,
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "foyda-hisobot.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setProductQuery("");
    setDateFrom("");
    setDateTo("");
  };

  const showAll = () => {
    setActiveSellerId(null);
    setSellerQuery("");
    setSellerOpen(false);
  };

  const toggleOpen = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="fs-wrap">
      <div className="fs-top fr-panel">
        <div className="seller-search" ref={sellerBoxRef}>
          <input
            className="input"
            placeholder="Mijoz ismi yoki telefoni bilan qidiring"
            value={sellerQuery}
            onChange={(e) => {
              setSellerQuery(e.target.value);
              setSellerOpen(true);
            }}
            onFocus={() => setSellerOpen(true)}
          />

          {sellerOpen && (
            <div className="seller-dd">
              {loadingSellers && <div className="dd-empty">Mijozlar yuklanmoqda...</div>}
              {!loadingSellers &&
                matchedSellers.map((seller) => (
                  <button
                    key={seller.id}
                    type="button"
                    className="seller-dd-item"
                    onClick={() => {
                      setActiveSellerId(seller.id);
                      setSellerQuery(
                        seller.phone ? `${seller.name} (${seller.phone})` : seller.name
                      );
                      setSellerOpen(false);
                    }}
                  >
                    <div
                      className="avatar"
                      style={
                        seller.avatar
                          ? { backgroundImage: `url(${seller.avatar})` }
                          : undefined
                      }
                    />
                    <div className="dd-body">
                      <div className="dd-name">{seller.name || "Noma'lum mijoz"}</div>
                      <div className="dd-phone">{seller.phone || "-"}</div>
                    </div>
                  </button>
                ))}
              {!loadingSellers && !matchedSellers.length && (
                <div className="dd-empty">Mos mijoz topilmadi</div>
              )}
            </div>
          )}
        </div>

        <button type="button" className="btn all" onClick={showAll}>
          Barchasi
        </button>

        <div className="fs-cards">
          <div className="mini-card fr-card">
            <div className="mini-title">Mijoz</div>
            <div className="mini-main">{activeSeller?.name || "Barchasi"}</div>
          </div>
          <div className="mini-card fr-card">
            <div className="mini-title">Savdosi</div>
            <div className="mini-main">{money(headerTotals.revenue)}</div>
          </div>
          <div className="mini-card fr-card">
            <div className="mini-title">Foydasi</div>
            <div className="mini-main">{money(headerTotals.profit)}</div>
          </div>
          <div className="mini-card fr-card">
            <div className="mini-title">Sotilgan tovar soni</div>
            <div className="mini-main">{fmtQty(headerTotals.qty)}</div>
          </div>
        </div>

        <div className="avatar-big">
          <div
            className="avatar"
            style={
              activeSeller?.avatar
                ? { backgroundImage: `url(${activeSeller.avatar})` }
                : undefined
            }
            title={activeSeller?.name || "Barchasi"}
          />
        </div>
      </div>

      <div className="fs-card fr-panel">
        <div className="fs-card-toolbar">
          <input
            className="input grow"
            placeholder="Mahsulot nomi yoki seriyasi bilan qidiring"
            value={productQuery}
            onChange={(e) => setProductQuery(e.target.value)}
          />

          <div className="date-range">
            <input
              className="input"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <span className="sep">-</span>
            <input
              className="input"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="spacer" />

          <button
            type="button"
            className="btn"
            onClick={resetFilters}
          >
            Filtrni tozalash
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => setRefreshKey((v) => v + 1)}
          >
            Yangilash
          </button>
          <button
            type="button"
            className="btn"
            onClick={doExport}
            disabled={!filteredSales.length}
          >
            Eksport JSON
          </button>
        </div>

        {error && <div className="fs-state fs-state-error">{error}</div>}

        <div className="fs-list">
          {loadingSales && <div className="empty">Savdolar yuklanmoqda...</div>}

          {!loadingSales &&
            filteredSales.map((sale) => {
              const totals = saleTotals(sale);
              const open = !!openMap[sale.id];

              return (
                <div key={sale.id} className="sale-block">
                  <button
                    type="button"
                    className="sale-head"
                    onClick={() => toggleOpen(sale.id)}
                  >
                    <div className="sh-left">
                      <div className="pill soft">{sale.sellerName}</div>
                      <div className="sale-note">{sale.note || "Izoh yo'q"}</div>
                    </div>

                    <div className="sh-mid">
                      <span className="muted">Vaqt:</span>
                      <b>{formatDateTime(sale.createdAt)}</b>
                      <span className="dot" />
                      <span className="muted">To'lov:</span>
                      <b>{sale.payLabel}</b>
                      <span className="dot" />
                      <span className="muted">Foyda:</span>
                      <b>{money(totals.profit)}</b>
                      <span className="dot" />
                      <span className="muted">Jami:</span>
                      <b>{money(totals.revenue)}</b>
                      {sale.soldBy && (
                        <>
                          <span className="dot" />
                          <span className="muted">Sotgan:</span>
                          <b>{sale.soldBy}</b>
                        </>
                      )}
                    </div>

                    <div className="sh-right">{open ? "▾" : "▸"}</div>
                  </button>

                  {open && (
                    <div className="sale-body">
                      <div className="row head">
                        <div>Mahsulot</div>
                        <div>Seriya</div>
                        <div>Tan narxi</div>
                        <div>Sana</div>
                        <div>Sotilgan narxi</div>
                        <div>Soni</div>
                        <div>Umumiy foyda</div>
                        <div>Jami</div>
                        <div>To'lov</div>
                      </div>

                      {sale.items.map((item) => (
                        <div key={item.id} className="row">
                          <div className="tight">{item.product}</div>
                          <div className="tight">{item.sku}</div>
                          <div className="mono">
                            {money(item.tanNarx, { minFrac: 2, maxFrac: 3 })}
                          </div>
                          <div className="mono">{formatDate(item.date)}</div>
                          <div className="mono">
                            {money(item.sotilganNarx, { minFrac: 2, maxFrac: 3 })}
                          </div>
                          <div className="mono">{fmtQty(item.qty)}</div>
                          <div className="mono">{money(item.lineProfit)}</div>
                          <div className="mono">{money(item.lineTotal)}</div>
                          <div
                            className={cx(
                              "pill",
                              item.payType === "naqd" ? "green" : "orange"
                            )}
                          >
                            {item.payType === "naqd" ? "Naqd" : "Nasiya"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

          {!loadingSales && !filteredSales.length && !error && (
            <div className="empty">Mos savdolar topilmadi</div>
          )}
        </div>
      </div>
    </div>
  );
}
